import { IconButton, MenuItem, Popover, Typography } from '@mui/material';
import { FC, useState, useEffect, MutableRefObject } from 'react';

type PopoverProps = Readonly<{
	layoutIcon: JSX.Element;
	forceCloseEvent?: Event;
	closeOnAction?: boolean;
	options: Readonly<{
		icon: JSX.Element;
		caption: JSX.Element | string;
		onAction: () => void;
		disabled?: boolean;
		ref?: MutableRefObject<HTMLLIElement | null>;
	} | null>[];
}>;

export const CustomPopover: FC<PopoverProps> = ({ layoutIcon, options, forceCloseEvent, closeOnAction = true }) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const open = Boolean(anchorEl);
	const id = open ? `popover-${Math.random()}` : undefined;
	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	useEffect(() => {
		if (forceCloseEvent?.type) {
			window.addEventListener(forceCloseEvent.type, handleClose);
		}

		return () => {
			if (forceCloseEvent?.type) {
				window.removeEventListener(forceCloseEvent.type, handleClose);
			}
		};
	}, [forceCloseEvent]);

	const createItemClickHandler = (action: () => void) => () => {
		if (closeOnAction) {
			handleClose();
		}
		action();
	};

	return (
		<>
			<IconButton data-ignore-pdf color={'default'} onClick={handleClick} sx={{ width: 20, height: 20, padding: 2 }}>
				{layoutIcon}
			</IconButton>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				{options.map((option, index) => {
					if (option === null) {
						return null;
					}
					return (
						<MenuItem
							key={`option-${index}`}
							onClick={createItemClickHandler(option.onAction)}
							sx={{ gap: 1, margin: 0.5, '&:hover': { borderRadius: 0.5 } }}
							ref={option.ref}
							disabled={option.disabled}
						>
							{option.icon}
							<Typography variant={'body1'}>{option.caption}</Typography>
						</MenuItem>
					);
				})}
			</Popover>
		</>
	);
};
